import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, firstValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';

export interface AppVersionCheckResponse {
  forceUpdate: boolean;
  latestVersion: string;
  updateUrl: { ios: string; android: string };
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class ForceUpdateService {

  private readonly http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  async check(): Promise<AppVersionCheckResponse> {
    // Force-update only applies to native app-store builds — a PWA has no "update from the
    // store" concept, it just gets the latest code on next load/service-worker update, so
    // skip the API call entirely rather than hitting the backend for a check that can never
    // meaningfully apply.
    if (!Capacitor.isNativePlatform()) {
      return { forceUpdate: false, latestVersion: '', updateUrl: { ios: '', android: '' } };
    }

    const appVersion = await this.getAppVersion();
    const platform = Capacitor.getPlatform();

    const response = this.http.get<AppVersionCheckResponse>(`${environment.apiServer}/v1/config/version-check`, {
        withCredentials: true, headers: { 'X-App-Version': appVersion, 'X-Platform': platform },
      })
      .pipe(
        catchError(() =>
          of<AppVersionCheckResponse>({
            forceUpdate: false,
            latestVersion: '',
            updateUrl: { ios: '', android: '' },
          })
        )
      );

    return await firstValueFrom(response);
  }

  private async getAppVersion(): Promise<string> {
    if (Capacitor.isNativePlatform()) {
      const info = await App.getInfo();
      return info.version;
    }
    return environment.buildVersion;
  }
}
